import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const SaleItem = ({ sale }) => {
  const isPro = sale.tier === 'pro';
  return (
    <span className="inline-flex items-center gap-2 px-5 py-1.5 mx-1" data-testid="ticker-item">
      <Zap className={`w-3.5 h-3.5 ${isPro ? 'text-emerald-300' : 'text-green-500'}`} />
      <span className="text-green-300/90 text-sm font-mono">{sale.buyer}</span>
      <span className="text-green-500/60 text-sm">unlocked</span>
      <span className={`text-xs font-bold px-2 py-0.5 rounded ${isPro ? 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/40' : 'bg-green-500/15 text-green-400 border border-green-500/30'}`}>
        {isPro ? 'PRO' : 'BASIC'}
      </span>
      {sale.location && <span className="text-green-500/50 text-xs">· {sale.location}</span>}
      <span className="text-green-500/50 text-xs">· {timeAgo(sale.timestamp)}</span>
      <span className="text-green-500/20">•</span>
    </span>
  );
};

export const SalesTicker = () => {
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let active = true;
    const load = () => {
      axios.get(`${API}/recent-sales?limit=10`)
        .then(res => {
          if (!active) return;
          setSales(res.data?.sales || []);
          setTotal(res.data?.total_sales || 0);
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000); // refresh every 30s for "live" feel
    return () => { active = false; clearInterval(id); };
  }, []);

  if (sales.length === 0) return null;

  // Duplicate the list so the -50% scroll loops seamlessly
  const loop = [...sales, ...sales];

  return (
    <div
      className="relative overflow-hidden border-y border-green-500/20 bg-black/40 backdrop-blur-sm"
      data-testid="sales-ticker"
    >
      <div className="max-w-7xl mx-auto flex items-center">
        <div className="hidden sm:flex items-center gap-2 shrink-0 px-4 py-2 border-r border-green-500/20 bg-green-500/5">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-xs font-bold uppercase tracking-wider" data-testid="ticker-total">
            {total.toLocaleString()} sales
          </span>
        </div>
        <div className="ticker-viewport relative flex-1 overflow-hidden py-1">
          <div className="ticker-track">
            {loop.map((sale, idx) => (
              <SaleItem key={`${sale.timestamp}-${idx}`} sale={sale} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
